import React from "react";
import { Calendar, IndianRupee, Package, Edit2, Trash2 } from "lucide-react";
import { InventoryPurchaseWithDetails, getPaymentStatusColor } from "@/types/inventory";

type PurchaseCardProps = {
  purchase: InventoryPurchaseWithDetails;
  onEdit?: (purchase: InventoryPurchaseWithDetails) => void;
  onDelete?: (purchase: InventoryPurchaseWithDetails) => void;
};

export function PurchaseCard({ purchase, onEdit, onDelete }: PurchaseCardProps) {
  const vendor = purchase.vendor;
  const product = purchase.product;

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1">
          {product && (
            <h3 className="font-semibold text-gray-900 mb-1">{product.name}</h3>
          )}
          {vendor && (
            <p className="text-sm text-gray-500">from {vendor.name}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span
            className={`px-2 py-1 rounded-full text-xs font-medium ${getPaymentStatusColor(
              purchase.payment_status
            )}`}
          >
            {purchase.payment_status === "paid"
              ? "Paid"
              : purchase.payment_status === "partial"
              ? "Partial"
              : "Pending"}
          </span>
          {(onEdit || onDelete) && (
            <div className="flex gap-1">
              {onEdit && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(purchase);
                  }}
                  className="p-1.5 text-gray-600 hover:text-[#5B8E87] hover:bg-gray-50 rounded transition-colors"
                  title="Edit purchase"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
              )}
              {onDelete && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(purchase);
                  }}
                  className="p-1.5 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                  title="Delete purchase"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <p className="text-xs text-gray-500">Quantity</p>
          <p className="font-medium text-gray-900 flex items-center gap-1">
            <Package className="w-3 h-3" />
            {purchase.quantity}
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-500">Total Cost</p>
          <p className="font-medium text-gray-900 flex items-center gap-1">
            <IndianRupee className="w-3 h-3" />
            {Number(purchase.total_cost).toFixed(2)}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2 text-xs text-gray-500">
        <Calendar className="w-3 h-3" />
        {new Date(purchase.purchase_date).toLocaleDateString()}
        {purchase.invoice_number && (
          <>
            <span>•</span>
            <span>Invoice: {purchase.invoice_number}</span>
          </>
        )}
      </div>

      {purchase.batch_number && (
        <div className="mt-2 text-xs text-gray-500">
          Batch: {purchase.batch_number}
        </div>
      )}
    </div>
  );
}
